import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card.jsx';
import { Button } from './ui/button.jsx';
import { Badge } from './ui/badge.jsx';
import {
    CheckCircle,
    XCircle,
    Clock,
    AlertTriangle,
    TrendingDown,
    Undo2,
    Eye,
    Shield,
    Cpu,
    HardDrive
} from 'lucide-react';
import { toast } from 'sonner';

function MLActionApproval({ clusterId, onActionComplete }) {
    const [pendingActions, setPendingActions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [processingAction, setProcessingAction] = useState(null);

    useEffect(() => {
        if (clusterId) {
            fetchPendingActions();
        }
    }, [clusterId]);

    const fetchPendingActions = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API}/ml/actions/pending-approval/${clusterId}`);
            setPendingActions(response.data.actions || []);
        } catch (error) {
            console.error('Failed to load pending actions:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = async (actionId) => {
        try {
            setProcessingAction(actionId);
            await axios.post(`${API}/ml/actions/${actionId}/approve`);
            toast.success('Action approved and queued for execution');
            fetchPendingActions();
            onActionComplete?.();
        } catch (error) {
            toast.error('Failed to approve action');
            console.error('Error approving action:', error);
        } finally {
            setProcessingAction(null);
        }
    };

    const handleReject = async (actionId) => {
        try {
            setProcessingAction(actionId);
            await axios.post(`${API}/ml/actions/${actionId}/reject`);
            toast.success('Action rejected');
            fetchPendingActions();
            onActionComplete?.();
        } catch (error) {
            toast.error('Failed to reject action');
            console.error('Error rejecting action:', error);
        } finally {
            setProcessingAction(null);
        }
    };

    // Format CPU value (handles both K8s strings like '600m' and numbers)
    const formatCpu = (value) => {
        if (!value && value !== 0) return 'N/A';
        if (typeof value === 'string') return value; // Already formatted like '600m'
        if (typeof value === 'number') {
            if (value < 1) return `${Math.round(value * 1000)}m`;
            return value.toFixed(2);
        }
        return String(value);
    };

    // Format memory value (handles both bytes and K8s strings like '1.2Gi')
    const formatMemory = (value) => {
        if (!value && value !== 0) return 'N/A';
        if (typeof value === 'string') return value; // Already formatted like '1.2Gi'
        if (typeof value === 'number') {
            // Assume bytes
            const mi = value / (1024 * 1024);
            if (mi < 1024) return `${Math.round(mi)}Mi`;
            return `${(mi / 1024).toFixed(1)}Gi`;
        }
        return String(value);
    };

    const formatResources = (resources) => {
        if (!resources) return 'N/A';
        const cpu = resources.cpu_request || resources.requests?.cpu || 'N/A';
        const memory = resources.memory_request || resources.requests?.memory || 'N/A';
        return `CPU: ${formatCpu(cpu)}, Memory: ${formatMemory(memory)}`;
    };

    const getConfidenceBadge = (confidence) => {
        const percent = Math.round(confidence * 100);
        if (percent >= 90) return <Badge className="bg-green-500">High ({percent}%)</Badge>;
        if (percent >= 75) return <Badge className="bg-yellow-500">Medium ({percent}%)</Badge>;
        return <Badge className="bg-orange-500">Low ({percent}%)</Badge>;
    };

    const totalPotentialSavings = pendingActions.reduce(
        (sum, action) => sum + (action.potential_monthly_savings || 0),
        0
    );

    if (loading) {
        return (
            <Card>
                <CardContent className="p-6">
                    <div className="flex items-center justify-center">
                        <Clock className="h-5 w-5 animate-spin mr-2" />
                        Loading pending ML recommendations...
                    </div>
                </CardContent>
            </Card>
        );
    }

    if (pendingActions.length === 0) {
        return (
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Shield className="h-5 w-5 text-green-500" />
                        ML Recommendations
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="text-center py-8 text-muted-foreground">
                        <CheckCircle className="h-12 w-12 mx-auto mb-3 text-green-500/50" />
                        <p>No pending recommendations requiring approval</p>
                        <p className="text-sm mt-2">ML-generated recommendations will appear here for review</p>
                    </div>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card>
            <CardHeader>
                <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <AlertTriangle className="h-5 w-5 text-amber-500" />
                        ML Recommendations Pending Approval
                        <Badge variant="outline" className="ml-2">
                            {pendingActions.length} pending
                        </Badge>
                    </div>
                    <div className="flex items-center gap-2">
                        <TrendingDown className="h-4 w-4 text-green-500" />
                        <span className="text-sm font-normal">
                            Potential savings: ${totalPotentialSavings.toFixed(2)}/month
                        </span>
                    </div>
                </CardTitle>
            </CardHeader>
            <CardContent>
                <div className="space-y-4">
                    {pendingActions.map((action) => (
                        <div
                            key={action.id}
                            className="border rounded-lg p-4 bg-card hover:bg-accent/5 transition-colors"
                        >
                            <div className="flex items-start justify-between">
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-2">
                                        <span className="font-medium">{action.workload_name}</span>
                                        <Badge variant="outline">{action.namespace}</Badge>
                                        <Badge variant="secondary">{action.workload_type || 'Deployment'}</Badge>
                                        {getConfidenceBadge(action.confidence)}
                                    </div>

                                    <div className="grid grid-cols-2 gap-4 text-sm">
                                        <div>
                                            <div className="text-muted-foreground mb-1 flex items-center gap-1">
                                                <Cpu className="h-3 w-3" /> Current Resources
                                            </div>
                                            <div className="font-mono text-xs bg-muted/50 p-2 rounded">
                                                {formatResources(action.current_resources)}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="text-muted-foreground mb-1 flex items-center gap-1">
                                                <HardDrive className="h-3 w-3" /> Recommended Resources
                                            </div>
                                            <div className="font-mono text-xs bg-green-500/10 p-2 rounded border border-green-500/20">
                                                {formatResources(action.recommended_resources)}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-4 mt-3 text-sm">
                                        <span className="text-green-500 font-medium">
                                            💰 ${action.potential_monthly_savings?.toFixed(2)}/month savings
                                        </span>
                                        <span className="text-muted-foreground">
                                            Method: {action.prediction_method || 'ML Prediction'}
                                        </span>
                                        {action.safety_checks?.is_protected_namespace && (
                                            <Badge variant="destructive" className="text-xs">Protected Namespace</Badge>
                                        )}
                                    </div>

                                    {/* Rollout Config Display */}
                                    {action.rollout_config && (
                                        <div className="mt-2 text-xs text-muted-foreground">
                                            <Shield className="h-3 w-3 inline mr-1" />
                                            Rolling update: min {action.rollout_config.min_available} replica available,
                                            PDB respected
                                        </div>
                                    )}
                                </div>

                                <div className="flex flex-col gap-2 ml-4">
                                    <Button
                                        size="sm"
                                        onClick={() => handleApprove(action.id)}
                                        disabled={processingAction === action.id}
                                        className="bg-green-600 hover:bg-green-700"
                                    >
                                        <CheckCircle className="h-4 w-4 mr-1" />
                                        Approve
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => handleReject(action.id)}
                                        disabled={processingAction === action.id}
                                        className="border-red-500/50 text-red-500 hover:bg-red-500/10"
                                    >
                                        <XCircle className="h-4 w-4 mr-1" />
                                        Reject
                                    </Button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {pendingActions.length > 0 && (
                    <div className="mt-4 flex justify-end gap-2">
                        <Button
                            variant="outline"
                            onClick={() => pendingActions.forEach(a => handleReject(a.id))}
                            className="border-red-500/50 text-red-500"
                        >
                            Reject All
                        </Button>
                        <Button
                            onClick={() => pendingActions.forEach(a => handleApprove(a.id))}
                            className="bg-green-600 hover:bg-green-700"
                        >
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Approve All ({pendingActions.length})
                        </Button>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

export default MLActionApproval;
